import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchProductByIdStart,
  createProductStart,
  updateProductStart,
  fetchDropdownsStart,
  setSelectedProduct,
  clearProductError
} from '../../store/reducers/productSlice';
import { Product, ProductLot, CreateProductRequest } from '../../services/productApi';

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
  },
  accordion: {
    marginTop: theme.spacing(3),
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  leftButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    '& > *': {
      marginRight: theme.spacing(2),
    },
  },
}));

const ProductEdit: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const {
    selectedProduct,
    productLoading,
    productError,
    productValidationErrors,
    dropdownUoMs,
    dropdownParts,
    dropdownPurchaseOrders,
    dropdownWorkOrders,
    dropdownSerials,
    dropdownsLoading
  } = useSelector((state: RootState) => state.product);

  // Form state
  const [formData, setFormData] = useState<Partial<Product>>({
    Name: '',
    Description: '',
    UoMId: '',
    Value: 0,
    PartId: '',
    Active: true,
    ProductLots: [{
      ProductLotNo: '',
      Description: '',
      TotalQty: 0,
      Serials: [],
      PurchaseOrders: [],
      WorkOrders: []
    }]
  });

  // UI state
  const [selectedPartId, setSelectedPartId] = useState<string>('');
  const [selectedPOs, setSelectedPOs] = useState<any[]>([]);

  useEffect(() => {
    dispatch(fetchDropdownsStart());
    dispatch(clearProductError());

    if (isEditMode && id) {
      dispatch(fetchProductByIdStart(id));
    } else {
      dispatch(setSelectedProduct(null));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (selectedProduct && isEditMode) {
      setFormData({
        ...selectedProduct,
        ProductLots: selectedProduct.ProductLots?.length 
          ? selectedProduct.ProductLots 
          : [{
              ProductLotNo: '',
              Description: '',
              TotalQty: 0,
              Serials: [],
              PurchaseOrders: [],
              WorkOrders: []
            }]
      });
      setSelectedPartId(selectedProduct.PartId || '');
    }
  }, [selectedProduct, isEditMode]);

  // Fetch Purchase Orders when Part changes
  useEffect(() => {
    if (selectedPartId) {
      dispatch({ type: 'product/fetchPurchaseOrdersByPart', payload: selectedPartId });
    }
  }, [selectedPartId, dispatch]);

  // Fetch Work Orders when Purchase Orders change
  useEffect(() => {
    if (selectedPOs.length > 0) {
      const poIds = selectedPOs.map(po => po.Id);
      dispatch({ type: 'product/fetchWorkOrdersByPOs', payload: poIds });
    }
  }, [selectedPOs, dispatch]);

  const handleInputChange = (field: keyof Product, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductLotChange = (index: number, field: keyof ProductLot, value: any) => {
    setFormData(prev => ({
      ...prev,
      ProductLots: prev.ProductLots?.map((lot, i) => 
        i === index ? { ...lot, [field]: value } : lot
      )
    }));
  };

  const handlePartChange = (event: any, newValue: any) => {
    const partId = newValue?.Id || '';
    setSelectedPartId(partId);
    handleInputChange('PartId', partId);
    
    // Reset dependent fields
    setSelectedPOs([]);
    handleProductLotChange(0, 'PurchaseOrders', []);
    handleProductLotChange(0, 'WorkOrders', []);
    handleProductLotChange(0, 'Serials', []);
  };

  const handlePOChange = (event: any, newValue: any) => {
    setSelectedPOs(newValue || []);
    handleProductLotChange(0, 'PurchaseOrders', newValue || []);
    
    // Reset dependent fields
    handleProductLotChange(0, 'WorkOrders', []);
    handleProductLotChange(0, 'Serials', []);
  };

  const handleWOChange = (event: any, newValue: any) => {
    const workOrders = newValue || [];
    handleProductLotChange(0, 'WorkOrders', workOrders);
    
    // Fetch serials for selected work orders
    if (workOrders.length > 0) {
      const woIds = workOrders.map((wo: any) => wo.Id);
      dispatch({ type: 'product/fetchSerialsByWorkOrders', payload: woIds });
    }
    
    // Reset serials
    handleProductLotChange(0, 'Serials', []);
  };

  const handleSerialChange = (event: any, newValue: any) => {
    const serials = newValue || [];
    handleProductLotChange(0, 'Serials', serials);
    handleProductLotChange(0, 'TotalQty', serials.length);
  };

  const handleSubmit = () => {
    const productData: CreateProductRequest = {
      Name: formData.Name || '',
      Description: formData.Description || '',
      UoMId: formData.UoMId,
      Value: formData.Value || 0,
      PartId: formData.PartId,
      ProductLots: formData.ProductLots,
      Active: formData.Active
    };

    if (isEditMode && selectedProduct?.id) {
      dispatch(updateProductStart({ 
        ...productData, 
        id: selectedProduct.id,
        ProductLots: formData.ProductLots as ProductLot[]
      } as Partial<Product>));
    } else {
      dispatch(createProductStart(productData));
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const handleCopyToPurchaseOrder = () => {
    // Navigate to packing list with product data
    navigate('/packing-lists/add', { state: { productLot: formData.ProductLots?.[0] } });
  };

  if (productLoading && isEditMode) {
    return (
      <Box p={3}>
        <Typography>Loading product...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 24 }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Product' : 'Add New Product'}
        </Typography>
      </Box>

      {productError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {productError}
        </Alert>
      )}

      {productValidationErrors.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          <ul>
            {productValidationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper style={{ padding: 24 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Product Name *"
              value={formData.Name || ''}
              onChange={(e) => handleInputChange('Name', e.target.value)}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Unit of Measurement *</InputLabel>
              <Select
                value={formData.UoMId || ''}
                onChange={(e) => handleInputChange('UoMId', e.target.value)}
                label="Unit of Measurement *"
                required
              >
                {dropdownUoMs.map((uom) => (
                  <MenuItem key={uom.Id} value={uom.Id}>
                    {uom.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description *"
              value={formData.Description || ''}
              onChange={(e) => handleInputChange('Description', e.target.value)}
              multiline
              rows={2}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={dropdownParts}
              getOptionLabel={(option) => option.Number || ''}
              value={dropdownParts.find(part => part.Id === selectedPartId) || null}
              onChange={handlePartChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Part"
                  placeholder="Select Part"
                />
              )}
              loading={dropdownsLoading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Value *"
              type="number"
              value={formData.Value || ''}
              onChange={(e) => handleInputChange('Value', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0 }}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.Active || false}
                  onChange={(e) => handleInputChange('Active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        {/* Product Lot Section */}
        <Accordion style={{ marginTop: 24 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Edit Product Lot</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Product Lot No: {formData.ProductLots?.[0]?.ProductLotNo || 'New Lot'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={dropdownPurchaseOrders}
                  getOptionLabel={(option) => option.Number || ''}
                  value={selectedPOs}
                  onChange={handlePOChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Purchase Orders"
                      placeholder="Select PO"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.Id}
                        label={option.Number}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  disabled={!selectedPartId}
                  loading={dropdownsLoading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Qty in Lot"
                  type="number"
                  value={formData.ProductLots?.[0]?.TotalQty || 0}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={dropdownWorkOrders.filter(wo => wo.WOStatusId === 2)} // Only approved WOs
                  getOptionLabel={(option) => option.WONum || ''}
                  value={formData.ProductLots?.[0]?.WorkOrders || []}
                  onChange={handleWOChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Work Orders"
                      placeholder="Select WO"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.Id}
                        label={option.WONum}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  disabled={selectedPOs.length === 0}
                  loading={dropdownsLoading}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Lot Description *"
                  value={formData.ProductLots?.[0]?.Description || ''}
                  onChange={(e) => handleProductLotChange(0, 'Description', e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={dropdownSerials}
                  getOptionLabel={(option) => option.SerialNum || ''}
                  value={formData.ProductLots?.[0]?.Serials || []}
                  onChange={handleSerialChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Serials"
                      placeholder="Select Serials"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.SerialNum}
                        label={option.SerialNum}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  disabled={(formData.ProductLots?.[0]?.WorkOrders || []).length === 0}
                  loading={dropdownsLoading}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="space-between" style={{ marginTop: 32 }}>
          <Box display="flex">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={productLoading}
              startIcon={<SaveIcon />}
              style={{ marginRight: 16 }}
            >
              {isEditMode ? 'Update' : 'Create'} Product
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </Box>

          {isEditMode && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCopyToPurchaseOrder}
              disabled={!selectedProduct?.id}
            >
              Copy to Purchase Order
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductEdit; 